import { Password, RawButton, Translation } from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import { minLength, numbersOnly, required } from '@ps/web-shared-validators';
import classNames from 'clsx';
import type { FormApi } from 'final-form';
import type { ReactNode, SyntheticEvent } from 'react';
import React, { useState } from 'react';
import type { FormRenderProps } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import { styles as sharedStyles } from '@swf/account';
import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import type { RequestError } from '@swf/core/errors/RequestError';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import type { Response } from '@swf/core/types/Account';
import { buildFormErrors } from '@swf/core/utils/forms';

import NextButton from '../NextButton/NextButton';

import {
  TXTCLI_As_you_have_opted_in_for_SMS_v_ELL,
  TXTCLI_Enter_SMS_Validation_Code,
  TXTCLI_Failure_to_validate_your_accou_ELL,
  TXTCLI_Must_be_at_least_N0_characters_ELL,
  TXTCLI_Our_systems_have_identified_po_ELL,
  TXTCLI_Validation_code,
  TXTCLI_Your_validation_code_has_been__ELL_1,
  TXTWCL_If_you_did_not_receive_a_code__ELL_1,
  TXTWCL_Please_enter_only_numerical_va_ELL
} from './LoginSMSForm.messages.json';

interface FormValues {
  validationCode: number;
}

const Form = createForm<FormValues>();

const LoginSMSForm: React.FC = () => {
  const { formatMessage } = useIntl();
  const request = useRequest();
  const [resent, setResent] = useState(false);
  const {
    modal: { close },
    auth: { logout }
  } = useDispatch<Dispatch>();

  const handleResendCode = async (form: FormApi<FormValues>, e: SyntheticEvent) => {
    e.preventDefault();
    request({
      service: '/api/v1-preview/auth/new-install/validate/resend',
      method: 'POST'
    }).subscribe({
      next: () => {
        form.reset();
        setResent(true);
      },
      error: logout
    });
  };

  const handleSubmit = async ({ validationCode }: FormValues, formApi: FormApi<FormValues>) => {
    try {
      await lastValueFrom(
        request({
          service: '/api/v1-preview/auth/new-install/validate',
          method: 'POST',
          http: true,
          body: { validationCode }
        })
      );
      close('/auth/login/sms');
    } catch (err) {
      setResent(false);
      formApi.change('validationCode');
      return buildFormErrors((err as RequestError<Response>).body);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      render={({
        form,
        submitting,
        submitError,
        hasValidationErrors
      }: FormRenderProps<FormValues>) => (
        <>
          {submitError && <StarsInfo type="error">{submitError}</StarsInfo>}
          {resent && (
            <StarsInfo>
              <Translation {...TXTCLI_Your_validation_code_has_been__ELL_1} />
            </StarsInfo>
          )}
          <p>
            <Translation {...TXTCLI_Our_systems_have_identified_po_ELL} />{' '}
            <Translation {...TXTCLI_As_you_have_opted_in_for_SMS_v_ELL} />
          </p>
          <Translation tagName="p" {...TXTCLI_Failure_to_validate_your_accou_ELL} />
          <Password
            autoFocus
            maxLength={9}
            id="validationCode"
            name="validationCode"
            numericKeyboard
            label={formatMessage(TXTCLI_Validation_code)}
            placeholder={formatMessage(TXTCLI_Enter_SMS_Validation_Code)}
            className={classNames(sharedStyles.input, {
              [sharedStyles.errors]: submitError
            })}
            validators={[
              required(null),
              numbersOnly(TXTWCL_Please_enter_only_numerical_va_ELL),
              minLength(4, { ...TXTCLI_Must_be_at_least_N0_characters_ELL, values: { 0: 4 } })
            ]}
          />
          <Translation
            tagName="p"
            {...TXTWCL_If_you_did_not_receive_a_code__ELL_1}
            values={{
              a: (msg: ReactNode) => (
                <RawButton
                  variant="text"
                  onMouseDown={(e: SyntheticEvent) => handleResendCode(form, e)}
                >
                  {msg}
                </RawButton>
              )
            }}
          />
          <ButtonGroup>
            <NextButton block type="submit" disabled={submitting || hasValidationErrors} />
          </ButtonGroup>
        </>
      )}
    />
  );
};

export default LoginSMSForm;
